(() => {
  // ===== helpers =====
  const isSame = (a, b) => String(a ?? "") === String(b ?? "");
  const uniq = (arr) => Array.from(new Set(arr.map((x) => String(x))));

  const closestPI = (el) => (el && el.closest ? el.closest("product-info") : null);

  const getMoneyFormat = () => {
    // 关键：不要写 "{{amount}}" 这种 Liquid 占位符到 .js 文件里（不会渲染）
    const mf =
      (window.theme && window.theme.money_format) ||
      (window.Shopify && window.Shopify.money_format) ||
      "";
    // 没拿到就返回空，后面即时价格就不做，避免“20.50 无€”闪烁
    return typeof mf === "string" ? mf : "";
  };

  const formatMoney = (cents) => {
    const mf = getMoneyFormat();
    if (mf && window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(cents, mf);
    }
    // 没 money_format 时，直接不格式化（交给主题原生 fetch 渲染）
    return null;
  };

  const getProductUrl = (pi) => {
    const any = pi.querySelector("[data-product-url]");
    const url = any?.dataset?.productUrl || pi.dataset.url || "";
    return url.split("?")[0].split("#")[0];
  };

  // 解析 option position：优先用 name 尾部 "-1/-2/-3"
  // 例：name="Metri-150673...-2"  => pos=2
  const parsePosFromName = (name) => {
    if (!name) return null;
    const m = String(name).match(/-(\d)$/);
    if (!m) return null;
    const n = Number(m[1]);
    return n >= 1 && n <= 3 ? n : null;
  };

  // 收集“组选项”
  const getOptionGroups = (pi) => {
    // 优先按 fieldset / .product-form__input 分组，但不依赖 data-option-index
    const containers = Array.from(pi.querySelectorAll(".product-form__input, fieldset, .variant-option")).filter(Boolean);

    const groups = [];

    // 1) 先从容器里找 radio/select；如果能解析到 pos，就加入
    containers.forEach((c) => {
      const radios = Array.from(c.querySelectorAll('input[type="radio"][name]'));
      const select = c.querySelector("select");

      // radio 组：用第一个 radio 的 name 尾部来定 pos
      if (radios.length) {
        const pos = parsePosFromName(radios[0].getAttribute("name"));
        if (pos) {
          groups.push({ pos, container: c, type: "radio" });
          return;
        }
      }

      // select 组：Ella 的 select 的 name 是 options[...]，不好解析
      // 但主题通常会在同容器里也有 data-option-index；如果没有，暂不处理 select（你的截图是 radio）
      if (select) {
        const posByData = c.dataset && c.dataset.optionIndex != null ? Number(c.dataset.optionIndex) + 1 : null;
        const pos = posByData >= 1 && posByData <= 3 ? posByData : null;
        if (pos) {
          groups.push({ pos, container: c, type: "select" });
        }
      }
    });

    // 2) 去重（同一个容器可能被多个选择器抓到）
    const uniqGroups = [];
    const seen = new Set();
    groups.forEach((g) => {
      const key = `${g.pos}::${g.container}`;
      if (seen.has(key)) return;
      seen.add(key);
      uniqGroups.push(g);
    });

    // pos 从小到大排序，保证 upstream 逻辑正确
    uniqGroups.sort((a, b) => a.pos - b.pos);
    return uniqGroups;
  };

  const getSelectedByPos = (pi, groups) => {
    const selected = {};
    groups.forEach((g) => {
      if (g.type === "radio") {
        const checked = g.container.querySelector('input[type="radio"]:checked');
        if (checked) selected[g.pos] = checked.value;
      } else if (g.type === "select") {
        const sel = g.container.querySelector("select");
        if (sel) selected[g.pos] = sel.value;
      }
    });
    return selected;
  };

  const fetchVariants = async (pi) => {
    if (pi.__vf3 && pi.__vf3.variants) return pi.__vf3.variants;

    const url = getProductUrl(pi);
    if (!url) return null;

    try {
      const res = await fetch(`${url}.js`, { credentials: "same-origin" });
      if (!res.ok) return null;
      const data = await res.json();
      const variants = data?.variants || null;
      pi.__vf3 = pi.__vf3 || {};
      pi.__vf3.variants = variants;
      return variants;
    } catch {
      return null;
    }
  };

  const findVariant = (variants, selectedByPos) => {
    const o1 = selectedByPos[1] ?? null;
    const o2 = selectedByPos[2] ?? null;
    const o3 = selectedByPos[3] ?? null;
    return (
      variants.find((v) => {
        if (o1 != null && !isSame(v.option1, o1)) return false;
        if (o2 != null && !isSame(v.option2, o2)) return false;
        if (o3 != null && !isSame(v.option3, o3)) return false;
        return true;
      }) || null
    );
  };

  // 判断某个候选值在当前 upstream 下是否存在/是否有可售
  const computeCandidate = (variants, pos, selectedByPos, candidate) => {
    const o1 = selectedByPos[1] ?? null;
    const o2 = selectedByPos[2] ?? null;

    let exists = false;
    let anyAvailable = false;

    for (const v of variants) {
      if (pos === 1) {
        if (!isSame(v.option1, candidate)) continue;
      } else if (pos === 2) {
        if (o1 != null && !isSame(v.option1, o1)) continue;
        if (!isSame(v.option2, candidate)) continue;
      } else if (pos === 3) {
        if (o1 != null && !isSame(v.option1, o1)) continue;
        if (o2 != null && !isSame(v.option2, o2)) continue;
        if (!isSame(v.option3, candidate)) continue;
      }
      exists = true;
      if (v.available) anyAvailable = true;
      if (anyAvailable) break;
    }

    return { exists, anyAvailable };
  };

  const applyOptionRules = (pi, variants) => {
    const groups = getOptionGroups(pi);
    if (!groups.length) return;

    const selectedByPos = getSelectedByPos(pi, groups);

    groups.forEach((g) => {
      if (g.type !== "radio") return; // 你当前是 radio，先把这块做稳

      const radios = Array.from(g.container.querySelectorAll('input[type="radio"][name]'));
      if (!radios.length) return;

      const currentChecked = g.container.querySelector('input[type="radio"]:checked');
      const currentVal = currentChecked ? currentChecked.value : null;

      const candidates = uniq(radios.map((r) => r.value));

      candidates.forEach((val) => {
        const { exists, anyAvailable } = computeCandidate(variants, g.pos, selectedByPos, val);

        // 不存在：hide（但如果它正好是当前选中的值，就不 hide，只 disabled）
        const shouldHide = !exists && !isSame(val, currentVal);
        // 不存在 or 全部缺货：disabled
        const shouldDisable = !exists || !anyAvailable;

        radios
          .filter((r) => isSame(r.value, val))
          .forEach((r) => {
            const wrap = r.closest("li") || r.closest("span") || r.parentElement;
            if (wrap) wrap.style.display = shouldHide ? "none" : "";
            r.disabled = shouldDisable;
            r.classList.toggle("disabled", shouldDisable);
          });
      });
    });
  };

  const instantPrice = (pi, variants) => {
    const mf = getMoneyFormat();
    if (!mf) return; // 没钱格式就不做即时价格，避免“20.50 无€”闪一下

    const groups = getOptionGroups(pi);
    const selectedByPos = getSelectedByPos(pi, groups);
    const v = findVariant(variants, selectedByPos);
    if (!v) return;

    const box = pi.querySelector(".price-product-container");
    if (!box) return;

    const cur = box.querySelector(".pp-price__current");
    const cmp = box.querySelector(".pp-price__compare");

    const curText = formatMoney(v.price);
    if (cur && curText) cur.textContent = curText;

    const cap = v.compare_at_price;
    if (cmp) {
      if (cap && Number(cap) > Number(v.price)) {
        const capText = formatMoney(cap);
        if (capText) {
          cmp.textContent = capText;
          cmp.style.display = "";
        }
      } else {
        cmp.textContent = "";
        cmp.style.display = "none";
      }
    }

    // 同步 add-to-cart variant id
    const form = pi.querySelector("form[action^='/cart/add']");
    const idInput = form?.querySelector('input[name="id"]');
    if (idInput) idInput.value = v.id;
  };

  // ===== scheduler (no page lock) =====
  const scheduleRun = (pi) => {
    pi.__vf3 = pi.__vf3 || {};
    if (pi.__vf3.running) return;
    if (pi.__vf3.queued) return;

    pi.__vf3.queued = true;

    requestAnimationFrame(async () => {
      pi.__vf3.queued = false;
      pi.__vf3.running = true;

      // 暂停 observer 防自触发
      try { pi.__vf3.mo?.disconnect(); } catch (_) {}

      try {
        const variants = await fetchVariants(pi);
        if (variants && Array.isArray(variants)) {
          applyOptionRules(pi, variants);
          instantPrice(pi, variants);
        }
      } finally {
        // 恢复 observer（监听范围收敛：variant-selects / 变体区块）
        try {
          const target = pi.__vf3.observeTarget || pi;
          pi.__vf3.mo?.observe(target, { childList: true, subtree: true });
        } catch (_) {}
        pi.__vf3.running = false;
      }
    });
  };

  const bindProductInfo = (pi) => {
    pi.__vf3 = pi.__vf3 || {};
    if (pi.__vf3.bound) return;
    pi.__vf3.bound = true;

    // 只监听变体输入变化
    pi.addEventListener("change", (e) => {
      const t = e.target;
      if (!t) return;
      if (t.matches('input[type="radio"], select')) scheduleRun(pi);
    });

    // 监听范围：variant-selects（没有就退回 product-info）
    const observeTarget = pi.querySelector("variant-selects") || pi;
    pi.__vf3.observeTarget = observeTarget;

    const mo = new MutationObserver(() => scheduleRun(pi));
    pi.__vf3.mo = mo;
    mo.observe(observeTarget, { childList: true, subtree: true });

    // 首次
    scheduleRun(pi);
  };

  const bindAll = () => {
    document.querySelectorAll("product-info").forEach(bindProductInfo);
    document.addEventListener("shopify:section:load", () => {
      document.querySelectorAll("product-info").forEach(bindProductInfo);
    });
  };

  window.addEventListener("load", bindAll);
})();

